import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { breedingAPI, horsesAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { toArray } from '../utils/apiData';
import { CardSkeleton } from './LoadingSkeletons';

const HorseBreedingHistory = ({ horseId, ownerTypeFilter = 'owned' }) => {
  const [breedingRecords, setBreedingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horseType, setHorseType] = useState('');

  const loadBreedingHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get horse details to know its type
      const horseResponse = await horsesAPI.getById(horseId);
      const horse = horseResponse.data;
      setHorseType(horse.type);

      const isStallion = (horse.type || '').toLowerCase() === 'stallion';

      if (isStallion) {
        const stallionRecords = await breedingAPI.getAll({
          stallion_id: horseId,
          owner_type: ownerTypeFilter,
          sort_by: 'breeding_date',
          sort_order: 'desc',
        });
        setBreedingRecords(Array.isArray(stallionRecords.data) ? stallionRecords.data : []);
      } else {
        const mareRecords = await breedingAPI.getAll({ mare_id: horseId });
        const stallionRecords = await breedingAPI.getAll({ stallion_id: horseId });

        const allRecords = [
          ...toArray(mareRecords.data, 'breeding records (mare)'),
          ...toArray(stallionRecords.data, 'breeding records (stallion)'),
        ];
        allRecords.sort((a, b) => {
          const dateA = new Date(a.breeding_date);
          const dateB = new Date(b.breeding_date);
          return dateB - dateA;
        });

        setBreedingRecords(allRecords);
      }
    } catch (error) {
      console.error('Error loading breeding history:', error);
    } finally {
      setLoading(false);
    }
  }, [horseId, ownerTypeFilter]);

  useEffect(() => {
    loadBreedingHistory();
  }, [loadBreedingHistory]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'foaled':
        return 'success';
      case 'confirmed':
        return 'primary';
      case 'bred':
        return 'info';
      case 'open':
        return 'warning';
      case 'aborted':
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateDaysInFoal = (record) => {
    if (!record.breeding_date) {
      return null;
    }

    try {
      const breedingDate = new Date(record.breeding_date);
      const endDate = record.actual_foaling_date 
        ? new Date(record.actual_foaling_date)
        : new Date();
      
      // Set time to midnight to avoid timezone issues
      breedingDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      const diffTime = endDate - breedingDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error('Error calculating days in foal:', error);
      return null;
    }
  };

  const formatMonthsAndDays = (days) => {
    if (days === null || days < 0) {
      return null;
    }

    if (days === 0) {
      return '0 days';
    }

    const months = Math.floor(days / 30);
    const remainingDays = days % 30;

    if (months === 0) {
      return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
    }

    if (remainingDays === 0) {
      return `${months} ${months === 1 ? 'month' : 'months'}`;
    }

    return `${months} ${months === 1 ? 'month' : 'months'}, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
  };

  if (loading) {
    return <CardSkeleton label="Loading breeding history…" />;
  }

  return (
    <Card>
      <CardContent>
        {breedingRecords.length === 0 ? (
          <Typography color="textSecondary">No breeding records found</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>
                    {horseType?.toLowerCase() === 'mare' ? 'Stallion' : 'Mare'}
                  </TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Days In Foal</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Foaling Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breedingRecords.map((record) => {
                  const isMare = record.mare_id === parseInt(horseId);
                  const mateName = isMare 
                    ? (record.stallion_registered_name || record.stallion_name)
                    : (record.mare_registered_name || record.mare_name);
                  
                  return (
                    <TableRow key={record.id}>
                      <TableCell>
                        {formatDate(record.breeding_date) ?? '-'}
                      </TableCell>
                      <TableCell>{mateName || 'Unknown'}</TableCell>
                      <TableCell>{record.breeding_method || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.pregnancy_status || 'Unknown'}
                          size="small"
                          color={getStatusColor(record.pregnancy_status)}
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isBred = (record.pregnancy_status || '').toString().toLowerCase() === 'bred';
                          const isOpen = (record.pregnancy_status || '').toString().toLowerCase() === 'open';
                          const bredSx = isBred ? { color: 'text.disabled' } : undefined;

                          if (isOpen) {
                            return '-';
                          }

                          const days = calculateDaysInFoal(record);
                          if (days === null) {
                            return '-';
                          }
                          const monthsDaysStr = formatMonthsAndDays(days);
                          return (
                            <Box>
                              <Typography variant="body2" sx={bredSx}>
                                {days} {days === 1 ? 'day' : 'days'}
                              </Typography>
                              {monthsDaysStr && (
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', ...(bredSx || { color: 'text.secondary' }) }}>
                                  {monthsDaysStr}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isBred = (record.pregnancy_status || '').toString().toLowerCase() === 'bred';
                          const isOpen = (record.pregnancy_status || '').toString().toLowerCase() === 'open';
                          const bredSx = isBred ? { color: 'text.disabled' } : undefined;

                          if (isOpen) {
                            return '-';
                          }

                          if (!record.due_date) {
                            return '-';
                          }

                          return record.due_date_based_on_override ? (
                            <Tooltip title={record.mare_gestation_days_override != null
                              ? `Calculated using mare's gestation override (${record.mare_gestation_days_override} days)`
                              : "Calculated using mare's gestation override"}>
                              <span>
                                <Typography component="span" sx={bredSx}>
                                  {formatDate(record.due_date)}
                                  <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
                                    (override)
                                  </Typography>
                                </Typography>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={bredSx}>
                              {formatDate(record.due_date)}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {formatDate(record.actual_foaling_date) ?? '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseBreedingHistory;

